import { saveErrorMessage } from '@/lib/modal-dirty';
import { updateProject, archiveProject } from '@/lib/data/projects';
import { createRoom } from '@/lib/data/rooms';
import { listDesignerContacts, addContactToProject } from '@/lib/data/contacts';
import { createNote, updateNote, deleteNote } from '@/lib/data/notes';
import { updateSpec } from '@/lib/data/specs';
import { uploadAndRecordFile, deleteFileRecord } from '@/lib/data/files';
import { upcomingEntries } from '@/lib/schedule-grouping';
import { shareOrFallback, countsAsShared, composeShareText } from '@/lib/share-sheet';
import {
  buildReminderMessage, reminderUrl, reminderPhone, reminderKindLabel,
  buildPortalInviteMessage,
  notificationStamp, canResend, resendLabel,
} from '@/lib/notification-messages';
import {
  listAssignmentsForProject, assignContractor, unassignContractor,
  rotateContractorToken, clearContractorToken, getActivePortalToken, buildContractorUrl,
} from '@/lib/data/subcontractors';
import {
  markNotificationShared, cancelNotification, recordNotificationSend,
  NOTIFICATION_KIND_LABEL, type NotificationRow,
} from '@/lib/data/notifications';
import {
  createChangeOrder, updateChangeOrder,
  deleteChangeOrder, sendChangeOrder, CHANGE_ORDER_STATUS_LABEL,
  recordChangeOrderApproval, closeChangeOrder, reviseChangeOrder,
  listChangeOrderRevisions, softDeleteChangeOrder, restoreChangeOrder, reopenChangeOrder,
  answerChangeOrderQuestion,
} from '@/lib/data/change-orders';
import { reviseWarning } from '@/lib/change-order-history';
import {
  changeOrderMessage, changeOrderShareTitle, changeOrderRecipient,
} from '@/lib/change-order-message';
import { changeOrdersAwaitingAnswer, awaitingAnswerLabel, unansweredCount } from '@/lib/change-order-events';
import { companyName } from '@/lib/branding';
import { currentFirm } from '@/lib/current-firm';
import { rotateShareToken, clearShareToken, buildShareUrl } from '@/lib/data/share';
import { portalLinkBase } from '@/lib/current-firm';
import { contactInitials } from '@/lib/avatar';
import { approvalStamp, completionStamp } from '@/lib/room-status';
import {
  contactOptionLabel, contactSummary, contactSearchKey, contactTypeLabel,
  findMasterContractor,
} from '@/lib/contact-display';
import { renderProjectScheduleSection } from '@/views/project-schedule';
import { renderProjectPunchSection } from '@/views/project-punch';
import { openNoteSend } from '@/views/note-send';
import { sameSnapshot } from '@/lib/page-cache';
import {
  loadProjectWorkspace, peekWorkspace, workspaceIsFresh, rememberWorkspace, forgetProject,
  type ProjectWorkspace,
} from '@/lib/data/project-workspace';
import { openContactModal } from '@/lib/contact-modal';
import { openScheduleModal } from '@/lib/schedule-modal';
import {
  upsertQuestionnaire, readResponses,
  QUESTIONNAIRE_FIELDS, type QuestionnaireKey, type QuestionnaireResponses,
} from '@/lib/data/questionnaires';
import {
  exportSpecs, exportContacts, exportPunch, exportPresentation, primeExportBranding, exportBranding,
} from '@/lib/export';
import { getMyProfile } from '@/lib/data/branding';
import { esc, fmtDate, fmtMoney, setPageTitle } from '@/lib/dom';
import { openModal } from '@/lib/modal';
import { openEditRoomModal } from '@/lib/room-edit';
import { toastError, toastSuccess } from '@/lib/toast';
import {
  ROOM_STATUS_LABEL, SPEC_STATUS_LABEL, SCHEDULE_STATUS_LABEL, TRADE_LABEL,
} from '@/lib/labels';
import { indexMaterialCategories } from '@/lib/material-categories';
import {
  createBudgetCategory, saveProjectBudget, saveBudgetLine,
  updateBudgetCategory, deleteBudgetCategory, budgetCategoryUsage,
} from '@/lib/data/budget';
import { dollarsToCents, centsToDollarInput } from '@/lib/money';
import {
  buildBudget, budgetBreakdown, varianceTone, hasNoTarget, laborLabel, groupSpecsByBudgetCategory,
  deleteCategoryWarning, type BudgetSummary,
} from '@/lib/budget';
import { explainBudget, changeOrderTotal } from '@/lib/change-order-budget';
import {
  changeOrderActions, approvalLine, signedMoney, partitionChangeOrders, deleteWarning,
} from '@/lib/change-order-ui';
import { openChangeOrderDetail, type ChangeOrderActionHandlers } from '@/views/change-order-detail';
import {
  sortSpecs, filterSpecs, nextSort, extendedCostCents, parseQuantity,
  type SpecSortKey, type SortDir,
} from '@/lib/spec-table';
import { resolveMediaSrc, uploadProjectMedia, removeMedia, signedUrl, fmtSize } from '@/lib/storage';
import { getShell } from '@/lib/shell-handle';
import type {
  ProjectRow, RoomRow, DesignerContactRow, NoteRow, SpecRow, FileRow,
  QuestionnaireRow, ScheduleEntryRow, NoteTag, ContractorAssignmentRow,
  ContractorPortalTokenRow, ChangeOrderRow, ChangeOrderRevisionRow, ChangeOrderEventRow,
  MaterialCategoryRow, BudgetCategoryRow,
} from '@/types/database';
import { emptyToNull } from '@/lib/form-values';


// All-materials table state. Module-level so it survives the re-render that
// follows every edit — resetting the sort on each save would be maddening.
let specSort: { key: SpecSortKey; dir: SortDir } = { key: 'item', dir: 'asc' };
let specCategoryFilter = 'all';
let specRoomFilter = 'all';

// Which budget categories are expanded. Module-level so a drill-down stays
// open across the re-render that every inline edit triggers.
const openBudgetRows = new Set<string>();

// Section keys stay stable so existing URLs keep working — only labels change.
const SECTIONS = [
  { key: 'dashboard',     label: 'Dashboard',           group: 'hub' },
  { key: 'rooms',         label: 'Interior',            group: 'hub' },
  { key: 'exterior',      label: 'Exterior',            group: 'hub' },
  { key: 'schedule',      label: 'Install Schedule',    group: 'hub' },
  { key: 'punch',         label: 'Punch List',          group: 'hub' },
  { key: 'budget',        label: 'Budget',              group: 'hub' },
  { key: 'specs',         label: 'Materials',           group: 'hub' },
  { key: 'files',         label: 'Files & Docs',        group: 'hub' },
  { key: 'contacts',      label: 'Contacts',            group: 'hub' },
  { key: 'notes',         label: 'Notes',               group: 'hub' },
  { key: 'change_orders', label: 'Change Orders',       group: 'hub' },
  { key: 'questionnaire', label: 'Style Questionnaire', group: 'tools' },
  { key: 'export',        label: 'Export & Print',      group: 'tools' },
] as const;
type SectionKey = typeof SECTIONS[number]['key'];

function isValidSection(s: string): s is SectionKey {
  return SECTIONS.some((x) => x.key === s);
}

/**
 * Guards against a slow answer painting over a newer page. Clicking Dashboard
 * then Budget used to leave whichever response came back last on screen.
 */
let renderSeq = 0;

export async function renderProjectDetail(
  root: HTMLElement, id: string, section: string = 'dashboard',
  opts: { force?: boolean } = {},
): Promise<void> {
  const sec: SectionKey = isValidSection(section) ? section : 'dashboard';
  const mine = ++renderSeq;
  const cached = peekWorkspace(id);
  const onScreen = root.querySelector<HTMLElement>('.project-detail')?.dataset.projectId === id;

  if (cached && !opts.force) {
    // Paint what we already have: switching sections then costs nothing at
    // all, and a refresh never blanks the page behind a spinner.
    announce(cached, sec);
    paint(root, cached, sec);
    if (workspaceIsFresh(id)) return;
  } else if (!onScreen) {
    // Only when there is nothing to look at. A forced reload — which is what
    // every save does — leaves the page up and swaps it when the new data
    // lands, instead of flashing "Loading project…" after every edit.
    root.innerHTML = `<section class="page"><div class="loading">Loading project…</div></section>`;
  }

  try {
    const ws = await loadProjectWorkspace(id);
    if (mine !== renderSeq) return;            // a newer navigation won
    if (!ws) {
      forgetProject(id);
      root.innerHTML = `<section class="page"><a class="back-link" href="#/projects">← All projects</a><div class="empty"><p>Project not found.</p></div></section>`;
      return;
    }
    // Skipping the repaint is only ever right for a BACKGROUND refresh that
    // found nothing new. A forced reload is someone asking for a repaint —
    // sometimes because data changed, sometimes because a filter, a sort or an
    // expanded row did, and none of those live in the data at all. Suppressing
    // it there silently killed the Budget drill-down and the materials
    // sort/filters (2026-09-30).
    const unchanged = !opts.force && cached && sameSnapshot(cached, ws);
    rememberWorkspace(id, ws);
    if (unchanged) return;                     // nothing moved; leave the DOM alone
    announce(ws, sec);
    paint(root, ws, sec);
  } catch (err) {
    if (mine !== renderSeq) return;
    // There is still a usable page on screen; say what happened and keep it.
    // NOT saveErrorMessage: nothing was being saved, and "your text is still
    // here" would be nonsense on a failed refresh.
    if (onScreen || cached) { toastError(refreshFailedMessage(navigator.onLine)); return; }
    root.innerHTML = `<section class="page"><a class="back-link" href="#/projects">← All projects</a><div class="auth-error">${esc(err instanceof Error ? err.message : 'Failed to load')}</div></section>`;
  }
}

/** A refresh that failed with a usable page already on screen. */
function refreshFailedMessage(online: boolean): string {
  return online
    ? 'Couldn’t refresh this project — showing the last version that loaded.'
    : 'You’re offline — showing the last version that loaded.';
}

/** Title and sidebar. Runs on a cached paint too, or the crumb goes stale. */
function announce(ws: ProjectWorkspace, sec: SectionKey): void {
  setPageTitle(ws.p.name);
  getShell()?.setContext({
    kind: 'project', projectId: ws.p.id, projectName: ws.p.name, activeSection: sec,
  });
}

function paint(
  root: HTMLElement,
  ws: ProjectWorkspace,
  activeSection: SectionKey,
): void {
  const {
    p, rooms, contacts, notes, roomColors, specs, files, questionnaire, punch, schedule,
    notifications, changeOrders, changeOrderEvents, assignments, materialCategories, budgetCategories,
    budgetLines, projectBudget, openFeedback, pendingReminders, portalTokens,
  } = ws;
  // After a WRITE: reload from the server, never from cache.
  const reload = (): void => { void renderProjectDetail(root, p.id, activeSection, { force: true }); };
  /**
   * After a change to how this page is DISPLAYED — an expanded budget row, a
   * sort, a filter — none of which the server knows or cares about. Repaints
   * from the data already in hand: instant, and no requests at all. These used
   * to call reload() and spend twenty-odd requests to redraw the same rows.
   */
  const repaint = (): void => { paint(root, ws, activeSection); };
  const cats = indexMaterialCategories(materialCategories);
  // Split once, used by the section and the Deleted panel below (0046).
  const { live: liveChangeOrders, deleted: deletedChangeOrders } = partitionChangeOrders(changeOrders);
  const awaitingAnswer = changeOrdersAwaitingAnswer(changeOrderEvents);
  const budget = buildBudget({
    specs, budgetCategories, materialCategories, budgetLines, projectBudget,
    // One number, one column. The hero and the Budget section both read this.
    budgetCents: p.budget_cents,
    // Approved change orders raise it to the revised budget (0038). The binned
    // ones are excluded here as well as on screen: an approved one can't be
    // binned (0046 refuses it), so this changes no total today — but a deleted
    // change order silently moving the budget is the bug it prevents.
    changeOrders: liveChangeOrders,
  });
  const budgetSpecs = groupSpecsByBudgetCategory(specs, materialCategories, budgetCategories);
  // Replaced the Milestones panel (decisions log #5). Same helper feeds the
  // client portal, so both surfaces show the same work.
  const upcoming = upcomingEntries(schedule, { limit: 8 });
  // Printed output carries the designer's branding. Once per session, not per
  // paint: this ran on every render, which meant a request on every section
  // click for a value only Settings changes.
  primeExportBranding(getMyProfile);

  root.innerHTML = `
    <section class="page project-detail" data-project-id="${esc(p.id)}">

      <div class="project-section" data-section="dashboard">

      <div class="hero-card ${p.hero_image_url ? 'has-image' : ''}">
        <div class="hero-image">
          ${p.hero_image_url
            ? `<img data-media-src="${esc(p.hero_image_url)}" alt="" />`
            : `<div class="hero-image-placeholder" aria-hidden="true"></div>`}
          <button class="hero-image-edit" data-action="hero-image" title="${p.hero_image_url ? 'Change image' : 'Add image'}">${p.hero_image_url ? 'Change' : '+ Add'}</button>
        </div>
        <div class="hero-info">
          <div class="eyebrow">${esc(p.client_name ?? 'No client')}</div>
          <h2>${esc(p.name)}</h2>
          ${p.address ? `<h3>${esc(p.address)}</h3>` : ''}
          <div class="info-grid">
            <div class="info-cell"><label>Start</label><span>${fmtDate(p.start_date)}</span></div>
            <div class="info-cell"><label>Target</label><span>${fmtDate(p.target_completion_date)}</span></div>
            <div class="info-cell"><label>Budget</label><span>${fmtMoney(budget.totalBudgetCents)}${
              budget.changeOrders.approvedCount > 0
                ? ` <span class="muted small">incl. ${budget.changeOrders.approvedCount} change${budget.changeOrders.approvedCount === 1 ? '' : 's'}</span>`
                : ''}</span></div>
            <div class="info-cell"><label>Interior</label><span>${rooms.length}</span></div>
            ${(() => {
              const gc = findMasterContractor(contacts, p.master_contractor_id);
              return `<div class="info-cell"><label>Contractor</label><span>${
                gc ? esc(gc.name) : '<span class="muted">Not set</span>'
              }</span></div>`;
            })()}
          </div>
          <div class="hero-actions">
            <button class="btn-edit-project" data-action="edit-project">Edit Project Profile</button>
            ${p.hero_image_url ? `<button class="btn-link btn-danger-link" data-action="hero-image-remove">Remove image</button>` : ''}
            <button class="btn-link btn-danger-link" data-action="archive-project">Archive</button>
          </div>
        </div>
        ${renderHeroBudget(budget, p.id)}
      </div>

      ${renderReminders(pendingReminders, portalTokens)}

      <section class="panel">
        <div class="section-bar">
          <div class="section-bar-left"><h3>Coming up</h3></div>
          <a class="btn-ghost" href="#/projects/${esc(p.id)}/schedule">View schedule</a>
        </div>
        ${renderUpcoming(upcoming, rooms, contacts, assignments)}
      </section>

      <section class="panel">
        <div class="section-bar"><div class="section-bar-left"><h3>Notifications</h3></div></div>
        ${renderNotificationsPanel(notifications, contacts, portalTokens)}
      </section>

      <section class="panel">
        <div class="section-bar"><div class="section-bar-left"><h3>Client share</h3></div></div>
        ${renderSharePanel(p)}
      </section>
      </div>

      ${(() => {
        // Location is the room's own column since migration 0027. It used to be
        // derived from the room TYPE, which was removed from the product.
        const interior = rooms.filter((r) => !r.is_exterior);
        const exterior = rooms.filter((r) => r.is_exterior);

        const renderGroup = (label: string, list: RoomRow[], addAction: string, addLabel: string, emptyMsg: string): string => {
          if (list.length === 0) {
            return `<div class="section-bar"><div class="section-bar-left"><h3>${label}</h3></div><button class="btn-ghost" data-action="${addAction}">${addLabel}</button></div>
              <div class="empty-inline">${emptyMsg}</div>`;
          }
          // No type filter: room types were removed on 2026-09-18.
          return `<div class="section-bar"><div class="section-bar-left"><h3>${label}</h3></div><button class="btn-ghost" data-action="${addAction}">${addLabel}</button></div>
            <div class="rooms-grid">${list.map((r) => renderRoomCard(r, p.id, specs, openFeedback.filter((f) => f.room_id === r.id).length)).join('')}</div>`;
        };

        return `
          <div class="project-section" data-section="rooms">
            <section class="panel">
              ${renderGroup('Interior', interior, 'add-room', '+ Add room', 'No interior rooms yet. Add the first one to start scoping work.')}
            </section>
          </div>
          <div class="project-section" data-section="exterior">
            <section class="panel">
              ${renderGroup('Exterior', exterior, 'add-exterior', '+ Add exterior', 'No exterior areas yet. Add things like the front porch, deck, gutters, or roof.')}
            </section>
          </div>
        `;
      })()}

      <div class="project-section" data-section="schedule" id="project-schedule-container"></div>

      <div class="project-section" data-section="punch" id="project-punch-container"></div>

      <div class="project-section" data-section="budget">
        ${renderBudgetSection(budget, budgetSpecs, budgetCategories, rooms, changeOrders)}
      </div>

      <div class="project-section" data-section="specs">
      <section class="panel">
        <div class="section-bar"><div class="section-bar-left"><h3>All materials</h3></div></div>
        ${renderAllSpecsTable(specs, rooms, materialCategories, cats)}
      </section>
      </div>

      <div class="project-section" data-section="files">
      <section class="panel">
        <div class="section-bar">
          <div class="section-bar-left"><h3>Files</h3></div>
          <label class="btn-ghost file-upload-label">
            + Upload
            <input type="file" id="file-upload-input" hidden multiple />
          </label>
        </div>
        <div class="upload-status" id="upload-status" hidden></div>
        ${files.length === 0
          ? `<div class="empty-inline">No files uploaded yet.</div>`
          : `<ul class="files-list">${files.map((f) => renderFile(f, rooms)).join('')}</ul>`}
      </section>
      </div>

      <div class="project-section" data-section="contacts">
      <section class="panel">
        <div class="section-bar">
          <div class="section-bar-left">
            <h3>Contacts</h3>
            ${contacts.length === 0 ? '' : `
              <input type="search" class="contact-search" id="contact-search"
                     placeholder="Search name, company, email…" autocomplete="off" />`}
          </div>
          <button class="btn-ghost" data-action="pick-contact">+ From my contacts</button>
          <button class="btn-ghost" data-action="add-contact">+ Add</button>
        </div>
        ${contacts.length === 0
          ? `<div class="empty-inline">No contacts yet.</div>`
          : `<ul class="contacts" id="contacts-list">${contacts.map((c) => renderContact(c, c.id === p.master_contractor_id, portalTokens.get(c.id) ?? null, p.name)).join('')}</ul>
             <div class="empty-inline" id="contacts-no-match" hidden>No contacts match that search.</div>`}
      </section>
      </div>

      <div class="project-section" data-section="notes">
      <section class="panel">
        <div class="section-bar">
          <div class="section-bar-left">
            <h3>Notes</h3>
            <span class="muted small">Yours — the client's link doesn't show these. Send one to share it.</span>
          </div>
          <button class="btn-ghost" data-action="add-note">+ Add</button>
        </div>
        ${notes.length === 0
          ? `<div class="empty-inline">No notes yet.</div>`
          : `<ul class="notes">${notes.map(renderNote).join('')}</ul>`}
      </section>
      </div>

      <div class="project-section" data-section="change_orders">
      <section class="panel">
        <div class="section-bar"><div class="section-bar-left"><h3>Change Orders</h3></div><button class="btn-ghost" data-action="add-change-order">+ Change order</button></div>
        ${renderChangeOrdersTable(liveChangeOrders, rooms, awaitingAnswer, changeOrderEvents)}
        ${renderDeletedChangeOrders(deletedChangeOrders, rooms)}
      </section>
      </div>

      <div class="project-section" data-section="questionnaire">
      <section class="panel">
        <div class="section-bar">
          <div class="section-bar-left"><h3>Style questionnaire</h3></div>
          <button class="btn-ghost" data-action="edit-questionnaire">${questionnaire ? 'Edit' : '+ Start'}</button>
        </div>
        ${renderQuestionnairePanel(questionnaire)}
      </section>
      </div>

      <div class="project-section" data-section="export">
      <section class="panel">
        <div class="section-bar"><div class="section-bar-left"><h3>Export &amp; print</h3></div></div>
        <div class="export-grid">
          <button class="export-card" data-action="export-specs">
            <h4>Materials sheet</h4>
            <p>All specs across rooms, with vendor, qty, ext. cost, delivery and status.</p>
          </button>
          <button class="export-card" data-action="export-contacts">
            <h4>Contact list</h4>
            <p>Project team — names, roles, companies, phone and email.</p>
          </button>
          <button class="export-card" data-action="export-punch">
            <h4>Punch list</h4>
            <p>Open and resolved punch items, grouped by room.</p>
          </button>
          <button class="export-card" data-action="export-presentation">
            <h4>Client presentation</h4>
            <p>Overview, palette, rooms, selections per room, questionnaire.</p>
          </button>
        </div>
      </section>
      </div>

    </section>
  `;

  // Show only the active section.
  root.querySelectorAll<HTMLElement>('.project-section').forEach((el) => {
    el.hidden = el.dataset.section !== activeSection;
  });

  // Paint the project-level Schedule section (rendered by a sibling module).
  const scheduleContainer = root.querySelector<HTMLElement>('#project-schedule-container');
  if (scheduleContainer) {
    renderProjectScheduleSection(scheduleContainer, {
      projectId: p.id, rooms, contacts, schedule, assignments, reload,
    });
  }

  // Paint the project-level Punch List (rendered by a sibling module).
  const punchContainer = root.querySelector<HTMLElement>('#project-punch-container');
  if (punchContainer) {
    renderProjectPunchSection(punchContainer, { project: p, rooms, contacts, punch, reload });
  }

  // Resolve any signed-URL placeholders for img tags.
  root.querySelectorAll<HTMLImageElement>('img[data-media-src]').forEach((img) => {
    const src = img.getAttribute('data-media-src')!;
    void resolveMediaSrc(src).then((url) => { if (url) img.src = url; });
  });

  // ── Wire actions ─────────────────────────────────────────────────────────
  root.querySelector('[data-action="edit-project"]')!.addEventListener('click', () => {
    openModal({
      title: 'Edit Project Profile',
      fields: [
        { name: 'name', label: 'Name', required: true, defaultValue: p.name },
        { name: 'client_name', label: 'Client', defaultValue: p.client_name },
        { name: 'address', label: 'Address', defaultValue: p.address },
        { name: 'description', label: 'Description', type: 'textarea', defaultValue: p.description },
        { name: 'start_date', label: 'Start date', type: 'date', defaultValue: p.start_date },
        { name: 'target_completion_date', label: 'Target completion', type: 'date', defaultValue: p.target_completion_date },
        { name: 'budget_dollars', label: 'Budget (USD)', type: 'number', defaultValue: p.budget_cents == null ? null : Math.round(p.budget_cents / 100) },
        {
          name: 'master_contractor_id', label: 'Master contractor', type: 'select',
          // Every project contact, not just those flagged Contractor: filtering
          // would leave this empty whenever the designer hasn't ticked the box,
          // with nothing on screen to explain why. The labels carry the types.
          options: [
            { value: '', label: contacts.length === 0 ? '— No contacts on this project —' : '— None —' },
            ...contacts.map((c) => ({ value: c.id, label: contactOptionLabel(c) })),
          ],
          defaultValue: p.master_contractor_id ?? '',
        },
      ],
      onSubmit: async (v) => {
        await updateProject(p.id, {
          name: String(v.name),
          client_name: emptyToNull(v.client_name),
          address: emptyToNull(v.address),
          description: emptyToNull(v.description),
          start_date: emptyToNull(v.start_date),
          target_completion_date: emptyToNull(v.target_completion_date),
          budget_cents: v.budget_dollars ? Math.round(Number(v.budget_dollars) * 100) : null,
          master_contractor_id: emptyToNull(v.master_contractor_id),
        });
        reload();
      },
    });
  });

  root.querySelector('[data-action="archive-project"]')!.addEventListener('click', async () => {
    if (!confirm('Archive this project? It will be hidden from the list (data preserved).')) return;
    await archiveProject(p.id);
    // Don't leave the archived version cached for the next visit.
    forgetProject(p.id);
    location.hash = '#/projects';
  });

  root.querySelector('[data-action="hero-image"]')!.addEventListener('click', () => {
    pickFile('image/*', async (file) => {
      const { path } = await uploadProjectMedia(file, p.id, 'hero');
      // Best-effort: drop the old image if it was a storage path (not a URL).
      if (p.hero_image_url && !/^https?:/i.test(p.hero_image_url)) {
        await removeMedia(p.hero_image_url).catch(() => {});
      }
      await updateProject(p.id, { hero_image_url: path });
      reload();
    });
  });

  root.querySelector('[data-action="hero-image-remove"]')?.addEventListener('click', async () => {
    if (!confirm('Remove the hero image?')) return;
    if (p.hero_image_url && !/^https?:/i.test(p.hero_image_url)) {
      await removeMedia(p.hero_image_url).catch(() => {});
    }
    await updateProject(p.id, { hero_image_url: null });
    reload();
  });

  // ── Client share actions ──
  root.querySelectorAll('[data-action="share-generate"]').forEach((el) => {
    el.addEventListener('click', async () => {
    const expiry = (root.querySelector<HTMLInputElement>('#share-expiry')?.value) || '';
    const expiresAt = expiry ? new Date(expiry).toISOString() : null;
    try {
      const token = await rotateShareToken(p.id, expiresAt);
      await navigator.clipboard?.writeText(buildShareUrl(token)).catch(() => {});
      toastSuccess('Share link generated and copied');
      reload();
    } catch (err) { toastError(err instanceof Error ? err.message : 'Failed to generate link'); }
    });
  });
  root.querySelectorAll('[data-action="share-copy"]').forEach((el) => {
    el.addEventListener('click', async () => {
      if (!p.share_token) return;
      await navigator.clipboard.writeText(buildShareUrl(p.share_token));
      const btn = el as HTMLButtonElement;
      const orig = btn.textContent;
      btn.textContent = 'Copied!';
      setTimeout(() => { btn.textContent = orig; }, 1500);
    });
  });
  root.querySelector('[data-action="share-revoke"]')?.addEventListener('click', async () => {
    if (!confirm('Revoke this share link? The client will lose access immediately.')) return;
    await clearShareToken(p.id);
    reload();
  });

  // ── File uploads ──
  const fileInput = root.querySelector<HTMLInputElement>('#file-upload-input')!;
  const uploadStatus = root.querySelector<HTMLDivElement>('#upload-status')!;
  fileInput.addEventListener('change', async () => {
    const list = Array.from(fileInput.files ?? []);
    if (list.length === 0) return;
    uploadStatus.hidden = false;
    let done = 0;
    for (const f of list) {
      uploadStatus.textContent = `Uploading ${f.name} (${done + 1} of ${list.length})…`;
      try { await uploadAndRecordFile(f, p.id, null); done++; }
      catch (err) {
        uploadStatus.textContent = `Failed: ${err instanceof Error ? err.message : 'upload error'}`;
        return;
      }
    }
    fileInput.value = '';
    reload();
  });

  root.querySelectorAll<HTMLAnchorElement>('[data-download-file]').forEach((a) => {
    a.addEventListener('click', async (e) => {
      e.preventDefault();
      const path = a.dataset.downloadFile!;
      const url = await signedUrl(path);
      window.open(url, '_blank', 'noopener');
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-delete-file]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const f = files.find((x) => x.id === btn.dataset.deleteFile)!;
      if (!confirm(`Delete "${f.name}"? This removes the file from storage.`)) return;
      await deleteFileRecord(f);
      reload();
    });
  });

  // A room is Interior or Exterior according to which page's button added it
  // (chosen 2026-09-18). No type dropdown: room types were removed, and asking
  // "interior or exterior?" on a page that already answered it is noise.
  const openAddRoomModal = (exterior: boolean): void => {
    openModal({
      title: exterior ? 'Add exterior area' : 'Add room',
      fields: [
        { name: 'name', label: 'Name', required: true },
        // No Status field (video 4): a new room is always Proposed, and status
        // is only meaningful once there's something to approve. The database
        // default supplies it.
      ],
      onSubmit: async (v) => {
        await createRoom({
          project_id: p.id,
          name: String(v.name),
          is_exterior: exterior,
          sort_order: rooms.length,
        });
        reload();
      },
    });
  };

  // NOTE: querySelectorAll, not querySelector — `add-room` appears twice (the
  // Dashboard quick tile and the Interior section-bar button). Binding only the
  // first is what made "Add Room" dead everywhere except the Dashboard.
  root.querySelectorAll('[data-action="add-room"]')
    .forEach((el) => el.addEventListener('click', () => openAddRoomModal(false)));
  root.querySelectorAll('[data-action="add-exterior"]')
    .forEach((el) => el.addEventListener('click', () => openAddRoomModal(true)));

  root.querySelectorAll<HTMLButtonElement>('[data-edit-room]').forEach((btn) => {
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      const room = rooms.find((r) => r.id === btn.dataset.editRoom)!;
      // Shared with the room page (src/lib/room-edit.ts) so the two can't drift.
      openEditRoomModal(room, { onSaved: reload, onDeleted: reload });
    });
  });

  root.querySelectorAll('[data-action="add-contact"]').forEach((el) => {
    el.addEventListener('click', () => openContactModal({ projectId: p.id, onDone: reload }));
  });
  // Sharing a contractor's portal link. Same synchronous-share rule as the
  // reminders panel: the URL is already on the button.
  root.querySelectorAll<HTMLButtonElement>('[data-send-portal]').forEach((btn) => {
    btn.addEventListener('click', () => {
      void shareOrFallback({
        title: 'Contractor portal',
        text: btn.dataset.msg ?? '',
        url: btn.dataset.url || undefined,
        phone: btn.dataset.phone || null,
      }).catch(() => toastError('Could not open the share sheet'));
    });
  });

  // ── Reminders ──
  // NOTE: this handler is deliberately NOT async at the top. shareOrFallback
  // calls navigator.share() straight away; awaiting anything first would spend
  // the user gesture and iOS would refuse to open the sheet.
  root.querySelectorAll<HTMLButtonElement>('[data-send-reminder]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.sendReminder!;
      const outcome = shareOrFallback({
        title: 'Reminder',
        text: btn.dataset.msg ?? '',
        url: btn.dataset.url || undefined,
        phone: btn.dataset.phone || null,
      });
      // Everything after the share resolves is fine to await.
      void outcome.then(async (o) => {
        if (!countsAsShared(o)) return;   // dismissed, copied or failed
        try {
          await markNotificationShared(id);
          reload();
        } catch (err) {
          toastError(err instanceof Error ? err.message : 'Could not mark as sent');
        }
      }).catch(() => toastError('Could not open the share sheet'));
    });
  });

  // Any message in the history can go out again — a contractor who ignored
  // the first one has to be chased, and this app's dispatcher is the
  // designer's own phone.
  root.querySelectorAll<HTMLButtonElement>('[data-resend]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.resend!;
      // Synchronously, inside the gesture — see share-sheet.ts.
      const outcome = shareOrFallback({
        title: 'Message',
        text: btn.dataset.msg ?? '',
        url: btn.dataset.url || undefined,
        phone: btn.dataset.phone || null,
      });
      void outcome.then(async (o) => {
        if (!countsAsShared(o)) return;
        try {
          const times = await recordNotificationSend(id);
          toastSuccess(times > 1 ? `Sent again — ${times} times in total` : 'Sent');
          reload();
        } catch (err) {
          toastError(saveErrorMessage(err, navigator.onLine));
        }
      }).catch(() => toastError('Could not open the share sheet'));
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-dismiss-reminder]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      try {
        await cancelNotification(btn.dataset.dismissReminder!);
        reload();
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not dismiss');
      }
    });
  });

  // ── Budget ──
  // Dollars in the UI, cents in the database. Convert at the boundary and
  // nowhere else; a stray /100 deeper in is how money bugs start.
  // Commit on blur rather than on input: a designer typing "1200" would
  // otherwise fire a write per keystroke, each one re-rendering under them.
  const onCommit = <T>(
    el: HTMLInputElement,
    parse: (raw: string) => T | null,
    fn: (value: T | null) => Promise<void>,
  ): void => {
    let last = el.value;
    el.addEventListener('blur', async () => {
      if (el.value === last) return;
      last = el.value;
      try {
        await fn(parse(el.value));
        reload();
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not save');
      }
    });
    el.addEventListener('keydown', (e) => { if (e.key === 'Enter') el.blur(); });
  };

  // Writes projects.budget_cents, NOT project_budgets.total_budget_cents.
  // This input and the hero's Budget figure are the same number now; editing
  // either one moves both. Editing the budget here and via Edit Project
  // Profile also agree, because that modal already wrote this column.
  root.querySelectorAll<HTMLInputElement>('[data-budget-total]').forEach((el) => {
    onCommit(el, dollarsToCents, async (cents) => {
      await updateProject(p.id, { budget_cents: cents });
    });
  });
  root.querySelectorAll<HTMLInputElement>('[data-budget-fee]').forEach((el) => {
    onCommit(el, dollarsToCents, (cents) => saveProjectBudget(p.id, { designer_fee_cents: cents ?? 0 }));
  });
  root.querySelectorAll<HTMLInputElement>('[data-budget-manual]').forEach((el) => {
    const id = el.dataset.budgetManual!;
    onCommit(el, dollarsToCents, (cents) => saveBudgetLine(p.id, id, { materials_manual_cents: cents ?? 0 }));
  });
  root.querySelectorAll<HTMLInputElement>('[data-budget-labor]').forEach((el) => {
    const id = el.dataset.budgetLabor!;
    onCommit(el, dollarsToCents, (cents) => saveBudgetLine(p.id, id, { labor_cents: cents ?? 0 }));
  });
  // Expand/collapse a category to see the materials behind its number.
  root.querySelectorAll<HTMLButtonElement>('[data-budget-expand]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const id = btn.dataset.budgetExpand!;
      if (openBudgetRows.has(id)) openBudgetRows.delete(id);
      else openBudgetRows.add(id);
      repaint();
    });
  });

  // Inline edits from the drill-down. Editing a cost here re-rolls the whole
  // budget on reload, which is the point — reconcile without leaving the page.
  root.querySelectorAll<HTMLInputElement>('[data-spec-cost]').forEach((el) => {
    const id = el.dataset.specCost!;
    onCommit(el, dollarsToCents, async (cents) => { await updateSpec(id, { unit_cost_cents: cents }); });
  });

  root.querySelectorAll<HTMLInputElement>('[data-spec-qty]').forEach((el) => {
    const id = el.dataset.specQty!;
    onCommit(el, parseQuantity, async (qty) => {
      // specs.quantity is NOT NULL — a blank or unusable entry leaves the
      // stored value alone rather than silently resetting it to 1 and changing
      // what the line costs. The reload puts the old number back on screen.
      if (qty === null) return;
      await updateSpec(id, { quantity: qty });
    });
  });

  root.querySelectorAll<HTMLSelectElement>('[data-spec-budget-cat]').forEach((sel) => {
    sel.addEventListener('change', async () => {
      const id = sel.dataset.specBudgetCat!;
      try {
        // '' clears the override so the material falls back to its material
        // category's default — which is not the same as "no category".
        await updateSpec(id, { budget_category_id: sel.value || null });
        // Re-filing moves the material into a different row; keep the
        // destination open so the designer can see where it landed.
        if (sel.value) openBudgetRows.add(sel.value);
        reload();
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not re-file material');
      }
    });
  });

  // Budget categories are shared by every project in the firm; renaming and
  // deleting one reaches beyond this page, which is what the warning says.
  root.querySelectorAll<HTMLButtonElement>('[data-rename-category]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const cat = budgetCategories.find((c) => c.id === btn.dataset.renameCategory);
      if (!cat) return;
      openModal({
        title: `Rename “${cat.name}”`,
        submitLabel: 'Save',
        fields: [{ name: 'name', label: 'Category name', required: true, defaultValue: cat.name }],
        onSubmit: async (v) => {
          await updateBudgetCategory(cat.id, String(v.name ?? ''));
          toastSuccess('Category renamed');
          reload();
        },
      });
    });
  });

  root.querySelectorAll<HTMLButtonElement>('[data-delete-category]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const cat = budgetCategories.find((c) => c.id === btn.dataset.deleteCategory);
      if (!cat) return;
      btn.disabled = true;
      try {
        const usage = await budgetCategoryUsage(cat.id);
        if (!confirm(deleteCategoryWarning(cat.name, usage))) return;
        await deleteBudgetCategory(cat.id);
        toastSuccess(`${cat.name} deleted`);
        reload();
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not delete that category');
      } finally {
        btn.disabled = false;
      }
    });
  });

  root.querySelectorAll('[data-action="add-budget-category"]').forEach((el) => {
    el.addEventListener('click', async () => {
      const name = prompt('Name for the new budget category:');
      if (!name || !name.trim()) return;
      try {
        await createBudgetCategory(name.trim());
        reload();
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not add category');
      }
    });
  });

  // All-materials table: sortable headers + the two filter dropdowns.
  // querySelectorAll throughout — there is one node per header, and the
  // singular form would leave every column but the first dead.
  root.querySelectorAll<HTMLElement>('[data-sort]').forEach((thEl) => {
    thEl.addEventListener('click', () => {
      specSort = nextSort(specSort, thEl.dataset.sort as SpecSortKey);
      repaint();
    });
  });
  root.querySelectorAll<HTMLSelectElement>('[data-spec-cat-filter]').forEach((sel) => {
    sel.addEventListener('change', () => { specCategoryFilter = sel.value; repaint(); });
  });
  root.querySelectorAll<HTMLSelectElement>('[data-spec-room-filter]').forEach((sel) => {
    sel.addEventListener('change', () => { specRoomFilter = sel.value; repaint(); });
  });

  root.querySelectorAll('[data-action="pick-contact"]').forEach((el) => {
    el.addEventListener('click', () => {
      void openContactPicker({
        projectId: p.id,
        alreadyOn: new Set(contacts.map((c) => c.id)),
        onDone: reload,
      });
    });
  });

  // Contacts search — client-side filter over the already-loaded list.
  const contactSearch = root.querySelector<HTMLInputElement>('#contact-search');
  contactSearch?.addEventListener('input', () => {
    const q = contactSearch.value.trim().toLowerCase();
    const rows = root.querySelectorAll<HTMLElement>('#contacts-list .contact');
    let visible = 0;
    rows.forEach((row) => {
      const match = q === '' || (row.dataset.search ?? '').includes(q);
      row.hidden = !match;
      if (match) visible++;
    });
    const noMatch = root.querySelector<HTMLElement>('#contacts-no-match');
    if (noMatch) noMatch.hidden = visible > 0;
  });
  root.querySelectorAll<HTMLButtonElement>('[data-edit-contact]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const c = contacts.find((x) => x.id === btn.dataset.editContact)!;
      openContactModal({ projectId: p.id, contact: c, onDone: reload });
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-contractor-portal]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const c = contacts.find((x) => x.id === btn.dataset.contractorPortal)!;
      void openContractorPortalModal({ contact: c, rooms, schedule, onDone: reload });
    });
  });

  root.querySelectorAll('[data-action="add-note"]').forEach((el) => {
    el.addEventListener('click', () => openNoteModal({ projectId: p.id, onDone: reload }));
  });
  root.querySelectorAll<HTMLButtonElement>('[data-edit-note]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const n = notes.find((x) => x.id === btn.dataset.editNote)!;
      openNoteModal({ projectId: p.id, note: n, onDone: reload });
    });
  });
  // 0048 — notes are the designer's, so sharing one is an act. No `async`
  // wrapper: the modal's own Send button needs the click's user activation
  // (share-sheet.ts), and this only opens the modal.
  root.querySelectorAll<HTMLButtonElement>('[data-send-note]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const n = notes.find((x) => x.id === btn.dataset.sendNote);
      if (!n) return;
      openNoteSend({
        project: p, note: n, contacts,
        onSent: (who) => { toastSuccess(`Note sent to ${who}`); reload(); },
      });
    });
  });

  // ── Change orders ──
  // One set of actions, shared by the list and the detail panel — two copies
  // would drift the moment either grows a confirmation.
  const coActions: ChangeOrderActionHandlers = {
    edit: (co) => openChangeOrderModal({ projectId: p.id, rooms, co, onDone: reload }),
    // ⚠️ THE GESTURE TRAP (share-sheet.ts): navigator.share() must be called
    // with no await before it, so the message, the recipient and the URL are
    // all resolved from what is already in memory. Nothing here fetches.
    //
    // What this replaced queued an SMS row for every contact with a phone,
    // through a dispatcher parked since Phase 19 — so the client was never
    // actually told, and the design fee was addressed to every subcontractor.
    send: async (co) => {
      if (!p.share_token) {
        toastError('Generate the client’s share link first — there’s nothing to send them yet.');
        return;
      }
      const to = changeOrderRecipient(contacts, p);
      const text = changeOrderMessage({
        co, projectName: p.name, clientName: to.name, fromName: companyName(exportBranding(), currentFirm()?.name),
      });
      const outcome = await shareOrFallback({
        title: changeOrderShareTitle(co, p.name), text, url: buildShareUrl(p.share_token), phone: to.phone,
      });
      if (!countsAsShared(outcome)) {
        if (outcome === 'copied') toastError('Copied the message — paste it into your messaging app.');
        else if (outcome === 'failed') toastError('Couldn’t open a way to send that message.');
        // 'dismissed': they changed their mind. The change order stays a draft,
        // because marking it Sent would tell the client's portal it went out.
        return;
      }
      try {
        await sendChangeOrder(co, {
          text: composeShareText({ text, url: buildShareUrl(p.share_token) }),
          contactId: to.contactId, phone: to.phone,
        });
        toastSuccess(`CO #${co.number} sent — the client can approve it from their link`);
        reload();
      } catch (err) {
        toastError(saveErrorMessage(err, navigator.onLine));
      }
    },
    recordApproval: (co) => {
      openModal({
        title: `Record approval — CO #${co.number}`,
        submitLabel: 'Mark approved',
        fields: [
          { name: 'approved_by', label: 'Approved by', required: true, defaultValue: p.client_name ?? '' },
          // Required, same as a room approval (0040): an approval nobody
          // wrote down the origin of is the record that settles nothing.
          { name: 'note', label: 'How it was approved (e.g. “By email, 9/19”)', type: 'textarea',
            voice: true, voiceKind: 'line', required: true },
        ],
        onSubmit: async (v) => {
          await recordChangeOrderApproval(co.id, String(v.approved_by ?? ''), String(v.note ?? ''));
          toastSuccess(`CO #${co.number} approved — it now counts toward the budget`);
          reload();
        },
      });
    },
    revise: async (co) => {
      if (!confirm(reviseWarning(co))) return;
      try {
        await reviseChangeOrder(co.id);
        toastSuccess(`CO #${co.number} reopened as a draft — the approved version is kept in its history`);
        reload();
      } catch (err) {
        toastError(saveErrorMessage(err, navigator.onLine));
      }
    },
    close: async (co, as) => {
      if (!confirm(as === 'rejected'
        ? `Mark CO #${co.number} as declined by the client?`
        : `Withdraw CO #${co.number}? It will no longer show as pending to you or the client.`)) return;
      try {
        await closeChangeOrder(co.id, as);
        reload();
      } catch (err) {
        toastError(saveErrorMessage(err, navigator.onLine));
      }
    },
    // 0046 — a withdrawn or declined change order back in play. Not a
    // revision: there is no approved version to file, and the database logs it
    // as 'reopened' rather than 'revised'.
    reopen: async (co) => {
      if (!confirm(`Reopen CO #${co.number} as a draft?\n\n`
        + `Its history is kept. You'll need to send it again for the client to approve it.`)) return;
      try {
        await reopenChangeOrder(co.id);
        toastSuccess(`CO #${co.number} reopened as a draft`);
        reload();
      } catch (err) {
        toastError(saveErrorMessage(err, navigator.onLine));
      }
    },
    remove: async (co) => {
      if (!confirm(deleteWarning(co))) return;
      try {
        await softDeleteChangeOrder(co.id);
        toastSuccess(`CO #${co.number} moved to Deleted`);
        reload();
      } catch (err) {
        toastError(saveErrorMessage(err, navigator.onLine));
      }
    },
    restore: async (co) => {
      try {
        await restoreChangeOrder(co.id);
        toastSuccess(`CO #${co.number} restored`);
        reload();
      } catch (err) {
        toastError(saveErrorMessage(err, navigator.onLine));
      }
    },
    answer: (co) => {
      openModal({
        title: `Answer the client — CO #${co.number}`,
        submitLabel: 'Send answer',
        fields: [{
          name: 'body', label: 'Your answer', type: 'textarea', voice: true, required: true,
        }],
        onSubmit: async (v) => {
          await answerChangeOrderQuestion(co.id, String(v.body ?? ''));
          toastSuccess('Answered — the client will see it on their link');
          reload();
        },
      });
    },
  };

  const findCo = (id: string | undefined): ChangeOrderRow | undefined =>
    changeOrders.find((x) => x.id === id);

  root.querySelectorAll('[data-action="add-change-order"]').forEach((el) => el.addEventListener('click', () => {
    openChangeOrderModal({ projectId: p.id, rooms, onDone: reload });
  }));
  root.querySelectorAll<HTMLButtonElement>('[data-co-detail]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const co = findCo(btn.dataset.coDetail);
      if (!co) return;
      // Only a change order that has actually been revised has history to
      // fetch, so the common case costs no query at all.
      const revisions = co.revision > 0
        ? await listChangeOrderRevisions(co.id).catch(() => [] as ChangeOrderRevisionRow[])
        : [];
      openChangeOrderDetail({ co, rooms, revisions, events: changeOrderEvents, actions: coActions });
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-approve-co]').forEach((btn) => {
    btn.addEventListener('click', () => { const co = findCo(btn.dataset.approveCo); if (co) coActions.recordApproval(co); });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-close-co]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const co = findCo(btn.dataset.closeCo);
      if (co) void coActions.close(co, btn.dataset.closeAs === 'rejected' ? 'rejected' : 'cancelled');
    });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-edit-co]').forEach((btn) => {
    btn.addEventListener('click', () => { const co = findCo(btn.dataset.editCo); if (co) coActions.edit(co); });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-send-co]').forEach((btn) => {
    // No `async` wrapper and no await before send(): the click's own user
    // activation is what navigator.share() needs (share-sheet.ts).
    btn.addEventListener('click', () => { const co = findCo(btn.dataset.sendCo); if (co) void coActions.send(co); });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-reopen-co]').forEach((btn) => {
    btn.addEventListener('click', () => { const co = findCo(btn.dataset.reopenCo); if (co) void coActions.reopen(co); });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-delete-co]').forEach((btn) => {
    btn.addEventListener('click', () => { const co = findCo(btn.dataset.deleteCo); if (co) void coActions.remove(co); });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-restore-co]').forEach((btn) => {
    btn.addEventListener('click', () => { const co = findCo(btn.dataset.restoreCo); if (co) void coActions.restore(co); });
  });
  root.querySelectorAll<HTMLButtonElement>('[data-answer-co]').forEach((btn) => {
    btn.addEventListener('click', () => { const co = findCo(btn.dataset.answerCo); if (co) coActions.answer(co); });
  });

  root.querySelectorAll('[data-action="add-schedule-entry"]').forEach((el) => {
    el.addEventListener('click', () => {
      // Tasks hang off a room, so there has to be one before you can add work.
      if (rooms.length === 0) { toastError('Add a room first — tasks are scheduled per room.'); return; }
      openScheduleModal({
        rooms, sortOrder: schedule.length, onDone: reload,
        contractors: contacts.filter((c) => c.is_contractor || c.is_subcontractor),
      });
    });
  });

  root.querySelector('[data-action="edit-questionnaire"]')!.addEventListener('click', () => {
    openQuestionnaireModal({
      projectId: p.id,
      existing: questionnaire,
      onDone: reload,
    });
  });

  root.querySelector('[data-action="export-specs"]')!.addEventListener('click', () => {
    try { exportSpecs(p, rooms, specs); }
    catch (e) { toastError(e instanceof Error ? e.message : 'Export failed'); }
  });
  root.querySelector('[data-action="export-contacts"]')!.addEventListener('click', () => {
    try { exportContacts(p, contacts); }
    catch (e) { toastError(e instanceof Error ? e.message : 'Export failed'); }
  });
  root.querySelector('[data-action="export-punch"]')!.addEventListener('click', () => {
    try { exportPunch(p, rooms, punch, contacts); }
    catch (e) { toastError(e instanceof Error ? e.message : 'Export failed'); }
  });
  root.querySelector('[data-action="export-presentation"]')!.addEventListener('click', () => {
    try { exportPresentation(p, rooms, specs, roomColors, questionnaire, []); }
    catch (e) { toastError(e instanceof Error ? e.message : 'Export failed'); }
  });

}

// ─── Sub-renderers ─────────────────────────────────────────────────────────

function renderRoomCard(r: RoomRow, _projectId: string, specs: SpecRow[], openFeedbackCount: number): string {
  const count = specs.filter((s) => s.room_id === r.id).length;
  // A finished room's most useful date is when it finished.
  const stamp = completionStamp(r) ?? approvalStamp(r);
  return `
    <a class="room-card" href="#/rooms/${esc(r.id)}">
      ${r.hero_image_url ? `<div class="room-card-thumb"><img data-media-src="${esc(r.hero_image_url)}" alt="" /></div>` : ''}
      <div class="room-card-body">
        <div class="room-card-head">
          <h3>${esc(r.name)}</h3>
          <button class="icon-btn" data-edit-room="${esc(r.id)}" title="Edit room">✎</button>
        </div>
        <div class="room-card-meta">
          <span class="chip chip-${esc(r.status)}"
                ${stamp ? `title="${esc(stamp)}"` : ''}>${esc(ROOM_STATUS_LABEL[r.status])}</span>
        </div>
        <div class="room-card-footer">
          <span>${count} spec${count === 1 ? '' : 's'}</span>
          ${openFeedbackCount > 0
            ? `<span class="chip chip-danger">${openFeedbackCount} change${openFeedbackCount === 1 ? '' : 's'} requested</span>`
            : ''}
        </div>
      </div>
    </a>
  `;
}

function renderFile(f: FileRow, rooms: RoomRow[]): string {
  const room = f.room_id ? rooms.find((r) => r.id === f.room_id) : null;
  const icon = f.file_type === 'image' ? '🖼' : f.file_type === 'pdf' ? '📄' : f.file_type === 'spreadsheet' ? '📊' : f.file_type === 'doc' ? '📝' : f.file_type === 'cad' ? '📐' : '📎';
  return `
    <li class="file-row">
      <span class="file-icon">${icon}</span>
      <div class="file-meta">
        <a href="#" class="file-name" data-download-file="${esc(f.storage_path)}">${esc(f.name)}</a>
        <div class="muted small">
          ${fmtSize(f.size_bytes)}
          ${room ? ` · ${esc(room.name)}` : ''}
          · ${fmtDate(f.created_at)}
        </div>
      </div>
      <button class="icon-btn" data-delete-file="${esc(f.id)}" title="Delete">🗑</button>
    </li>
  `;
}

/**
 * Budget summary in the hero card's right column.
 *
 * Read-only on purpose. Every figure here is editable in the Budget section,
 * and giving the same field two edit surfaces is how the hero and the budget
 * table drifted apart in the first place. "View budget" is the way through,
 * matching the Coming up panel's summary + "View schedule" pattern.
 *
 * This reclaims the column the Progress panel vacated in Phase 13 rather than
 * adding one, so the existing `max-width: 1000px` collapse keeps working.
 */
function renderHeroBudget(b: BudgetSummary, projectId: string): string {
  const bd = budgetBreakdown(b);
  const varianceClass = varianceTone(b);

  return `
    <aside class="hero-budget">
      <h4>Budget</h4>

      <div class="hero-budget-total">
        <label>${b.changeOrders.approvedCount > 0 ? 'Revised budget' : 'Total budget'}</label>
        <strong>${b.totalBudgetCents == null
          ? '<span class="muted">Not set</span>'
          : fmtMoney(b.totalBudgetCents)}</strong>
        ${b.changeOrders.approvedCount > 0 && b.originalBudgetCents != null ? `
          <div class="muted small">${esc(fmtMoney(b.originalBudgetCents))} original ${esc(signedMoney(b.changeOrders.approvedTotalCents))} in changes</div>` : ''}
      </div>

      <dl class="hero-budget-lines">
        <div><dt>Labor</dt><dd>${fmtMoney(bd.laborCents)}</dd></div>
        <div><dt>Materials</dt><dd>${fmtMoney(bd.materialsCents)}</dd></div>
        <div><dt>Fees</dt><dd>${fmtMoney(bd.feesCents)}</dd></div>
        <div class="hero-budget-sum"><dt>Total</dt><dd>${fmtMoney(bd.totalCents)}</dd></div>
      </dl>

      <div class="hero-budget-variance">
        <label>Variance</label>
        <strong class="${varianceClass}">${
          hasNoTarget(b) ? 'No target set' : fmtMoney(b.varianceCents!)
        }</strong>
      </div>

      <a class="btn-ghost" href="#/projects/${esc(projectId)}/budget">View budget</a>
    </aside>`;
}

/**
 * The budget table.
 *
 * Money columns are right-aligned and every editable cell is a number input
 * that commits on blur — a designer reconciling a budget types across a row,
 * and a modal per cell would be unusable.
 */
function renderBudgetSection(
  b: BudgetSummary,
  specsByCategory: Map<string, SpecRow[]>,
  budgetCategories: BudgetCategoryRow[],
  rooms: RoomRow[],
  changeOrders: ChangeOrderRow[],
): string {
  const roomName = new Map(rooms.map((r) => [r.id, r.name] as const));
  const money = (c: number): string => fmtMoney(c);
  const varianceClass = varianceTone(b);

  const bd = budgetBreakdown(b);
  const why = explainBudget({
    originalCents: b.originalBudgetCents,
    totals: b.changeOrders,
    materialsCents: bd.materialsCents,
    laborCents: bd.laborCents,
    baseFeeCents: b.designerFeeCents,
  });
  // Each figure says what it's made of (2026-09-19): once change orders move
  // the numbers, a bare total invites "wait, where did that come from?".
  const header = `
    <section class="panel budget-header">
      <div class="budget-figures">
        <label class="budget-figure">
          <span>Original budget</span>
          <input type="number" step="1" min="0" data-budget-total
                 value="${esc(centsToDollarInput(b.originalBudgetCents))}"
                 placeholder="Not set" />
          <small class="budget-why">${esc(why.original)}</small>
        </label>
        <div class="budget-figure">
          <span>Approved changes</span>
          <strong>${esc(b.changeOrders.approvedCount > 0 ? signedMoney(b.changeOrders.approvedTotalCents) : '—')}</strong>
          <small class="budget-why">${esc(why.approvedChanges)}</small>
        </div>
        <div class="budget-figure">
          <span>Revised budget</span>
          <strong>${b.totalBudgetCents == null ? '<span class="muted">Not set</span>' : money(b.totalBudgetCents)}</strong>
          <small class="budget-why">${esc(why.revised)}</small>
        </div>
        <div class="budget-figure">
          <span>Projected total</span>
          <strong>${money(b.projectTotalCents)}</strong>
          <small class="budget-why">${esc(why.projected)}</small>
        </div>
        <div class="budget-figure">
          <span>Variance</span>
          <strong class="${varianceClass}">${
            hasNoTarget(b) ? 'No target set' : money(b.varianceCents!)
          }</strong>
          <small class="budget-why">${esc(why.variance)}</small>
        </div>
      </div>
      ${why.pending ? `<div class="budget-note">${esc(why.pending)}</div>` : ''}
      ${b.uncategorizedSpecCount > 0 ? `
        <div class="budget-note">
          ${b.uncategorizedSpecCount} material${b.uncategorizedSpecCount === 1 ? '' : 's'}
          ${b.uncategorizedSpecCount === 1 ? 'has' : 'have'} no budget category — counted below
          under <strong>Uncategorized</strong>. Set a category on the material, or map its
          material category, to file it.
        </div>` : ''}
      ${b.doubleCountedCategoryIds.length > 0 ? `
        <div class="budget-warn">
          ${b.doubleCountedCategoryIds.length} categor${b.doubleCountedCategoryIds.length === 1 ? 'y has' : 'ies have'}
          both room materials and a manual amount. These may double-count — review the
          highlighted rows.
        </div>` : ''}
    </section>`;

  const rows = b.rows.map((r) => {
    const open = openBudgetRows.has(r.categoryId);
    const mine = specsByCategory.get(r.categoryId) ?? [];
    return `
    <tr class="${r.possibleDoubleCount ? 'budget-row-warn' : ''} ${r.isUncategorized ? 'budget-row-un' : ''}">
      <td>
        <button class="budget-disclose" data-budget-expand="${esc(r.categoryId)}"
                aria-expanded="${open ? 'true' : 'false'}"
                ${mine.length === 0 ? 'disabled title="No materials roll into this category"' : ''}>
          <span class="budget-caret">${mine.length === 0 ? '' : (open ? '▾' : '▸')}</span>${esc(r.name)}
        </button>
        ${r.possibleDoubleCount ? `<span class="chip chip-danger">may double-count</span>` : ''}
        ${r.isUncategorized ? '' : `
          <span class="budget-cat-actions">
            <button class="icon-btn" data-rename-category="${esc(r.categoryId)}" title="Rename this category">✎</button>
            <button class="icon-btn" data-delete-category="${esc(r.categoryId)}" title="Delete this category">🗑</button>
          </span>`}
      </td>
      <td class="num">${r.specCount > 0
        ? `${money(r.rolledMaterialsCents)} <span class="muted">(${r.specCount})</span>`
        : '<span class="muted">—</span>'}</td>
      <td class="num">${r.isUncategorized
        ? '<span class="muted">—</span>'
        : `<input type="number" step="1" min="0" class="budget-input"
                  data-budget-manual="${esc(r.categoryId)}"
                  value="${esc(centsToDollarInput(r.manualMaterialsCents))}"
                  placeholder="0" />`}</td>
      <td class="num">${r.isUncategorized
        ? '<span class="muted">—</span>'
        : `<input type="number" step="1" min="0" class="budget-input"
                  data-budget-labor="${esc(r.categoryId)}"
                  value="${esc(centsToDollarInput(r.laborCents))}"
                  placeholder="0" />`}</td>
      <td class="num"><strong>${money(r.totalCents)}</strong></td>
    </tr>
    ${!open || mine.length === 0 ? '' : `
      <tr class="budget-drill-row"><td colspan="5">
        <table class="budget-drill">
          <thead><tr>
            <th>Item</th><th>Room</th><th class="num">Qty</th>
            <th class="num">Unit cost</th><th class="num">Extended</th>
            <th>Budget category</th><th></th>
          </tr></thead>
          <tbody>${mine.map((sp) => `
            <tr>
              <td>${esc(sp.item)}</td>
              <td class="muted">${esc(roomName.get(sp.room_id) ?? '—')}</td>
              <td class="num">
                <input type="number" step="0.01" min="0" class="budget-input budget-qty"
                       data-spec-qty="${esc(sp.id)}" value="${esc(String(sp.quantity))}" />
                ${sp.unit ? `<span class="muted">${esc(sp.unit)}</span>` : ''}
              </td>
              <td class="num">
                <input type="number" step="1" min="0" class="budget-input"
                       data-spec-cost="${esc(sp.id)}"
                       value="${esc(centsToDollarInput(sp.unit_cost_cents))}"
                       placeholder="0" />
              </td>
              <td class="num">${money(extendedCostCents(sp) ?? 0)}</td>
              <td>
                <select class="budget-select" data-spec-budget-cat="${esc(sp.id)}">
                  <option value=""${sp.budget_category_id ? '' : ' selected'}>
                    ${sp.budget_category_id ? '— Use material default —' : '— Material default —'}
                  </option>
                  ${budgetCategories.map((c) => `<option value="${esc(c.id)}"${
                    sp.budget_category_id === c.id ? ' selected' : ''
                  }>${esc(c.name)}</option>`).join('')}
                </select>
              </td>
              <td><a class="btn-link" href="#/rooms/${esc(sp.room_id)}">Open room</a></td>
            </tr>`).join('')}</tbody>
        </table>
        ${r.isUncategorized ? `
          <div class="budget-note" style="margin-top:10px">
            Setting a budget category here files just that material. To file every
            material of a kind at once, map its material category instead.
          </div>` : ''}
      </td></tr>`}`;
  }).join('');

  return `${header}
    <section class="panel">
      <div class="section-bar">
        <div class="section-bar-left"><h3>Budget by category</h3></div>
        <button class="btn-ghost" data-action="add-budget-category">+ Add category</button>
      </div>
      <div class="table-wrap"><table class="specs-table budget-table">
        <thead><tr>
          <th>Category</th>
          <th class="num">From rooms</th>
          <th class="num">Manual materials</th>
          <th class="num">${esc(laborLabel('designer'))}</th>
          <th class="num">Total</th>
        </tr></thead>
        <tbody>${rows}</tbody>
        <tfoot>
          <tr>
            <td colspan="4">Subtotal</td>
            <td class="num"><strong>${money(b.subtotalCents)}</strong></td>
          </tr>
          <tr>
            <td colspan="4">
              Designer fee
              <input type="number" step="1" min="0" class="budget-input budget-fee"
                     data-budget-fee value="${esc(centsToDollarInput(b.designerFeeCents))}"
                     placeholder="0" />
            </td>
            <td class="num"><strong>${money(b.designerFeeCents)}</strong></td>
          </tr>
          ${b.changeOrderFeeCents !== 0 ? `
          <tr>
            <td colspan="4">Design fees on approved change orders</td>
            <td class="num"><strong>${money(b.changeOrderFeeCents)}</strong></td>
          </tr>` : ''}
          <tr class="budget-grand">
            <td colspan="4">Project total</td>
            <td class="num"><strong>${money(b.projectTotalCents)}</strong></td>
          </tr>
        </tfoot>
      </table></div>
    </section>
    ${renderBudgetChangeOrders(changeOrders, rooms)}`;
}

/**
 * The change orders behind the "Approved changes" figure — and the pending
 * ones that would move it — so the budget explains itself (0038).
 */
function renderBudgetChangeOrders(changeOrders: ChangeOrderRow[], rooms: RoomRow[]): string {
  const shown = changeOrders.filter((co) => co.status === 'acknowledged' || co.status === 'sent');
  if (shown.length === 0) return '';
  const roomName = new Map(rooms.map((r) => [r.id, r.name] as const));
  return `
    <section class="panel">
      <div class="section-bar">
        <div class="section-bar-left"><h3>Change orders in this budget</h3></div>
        <a class="btn-ghost" href="#/projects/${esc(changeOrders[0]!.project_id)}/change_orders">All change orders</a>
      </div>
      <div class="table-wrap"><table class="specs-table">
        <thead><tr>
          <th>#</th><th>Title</th><th>Rooms</th><th class="num">Work</th><th class="num">Design fee</th>
          <th class="num">Total</th><th>Status</th>
        </tr></thead>
        <tbody>${shown.map((co) => `
          <tr class="${co.status === 'sent' ? 'budget-row-pending' : ''}">
            <td>CO #${co.number}</td>
            <td>${esc(co.title)}${approvalLine(co) ? `<div class="muted small">${esc(approvalLine(co)!)}</div>` : ''}</td>
            <td class="muted">${co.affected_room_ids.length === 0 ? 'All rooms'
              : esc(co.affected_room_ids.map((id) => roomName.get(id) ?? '?').join(', '))}</td>
            <td class="num">${esc(signedMoney(co.cost_delta_cents))}</td>
            <td class="num">${esc(signedMoney(co.designer_fee_cents))}</td>
            <td class="num"><strong>${esc(signedMoney(changeOrderTotal(co)))}</strong></td>
            <td><span class="chip chip-${esc(co.status)}">${esc(co.status === 'sent' ? 'Awaiting client — not counted' : CHANGE_ORDER_STATUS_LABEL[co.status])}</span></td>
          </tr>`).join('')}
        </tbody>
      </table></div>
    </section>`;
}

/**
 * "Coming up" — the dashboard panel that replaced Milestones.
 *
 * Shows what the designer should be looking at next: date, task, room, who's
 * assigned, and status. Read-only by design; editing lives in Install Schedule,
 * and a second place to edit the same rows is a second place for them to drift.
 */
/**
 * "Reminders to send" — the queue Twilio would have drained.
 *
 * Every reminder's message text, portal URL and recipient are baked into data
 * attributes HERE, at render time. That is not an optimization: navigator.share()
 * must be called synchronously inside the click handler, and any await before it
 * consumes the user gesture — iOS Safari then throws NotAllowedError and the
 * sheet never opens. Nothing may be fetched between the tap and the share.
 */
/**
 * The clickable link for one reminder.
 *
 * The stored `portal_url` is a bare `#/contractor/<token>` (the trigger has no
 * host to put in front of it), so the host is added here — and the
 * contractor's CURRENT token wins over the one the reminder was queued with,
 * which may since have been rotated.
 */
function linkFor(
  n: NotificationRow,
  portalTokens: Map<string, ContractorPortalTokenRow>,
): string | null {
  const token = n.contact_id ? portalTokens.get(n.contact_id)?.token : null;
  return reminderUrl(n, {
    base: portalLinkBase(),
    liveUrl: token ? buildContractorUrl(token) : null,
  });
}

function renderReminders(
  pending: NotificationRow[],
  portalTokens: Map<string, ContractorPortalTokenRow>,
): string {
  if (pending.length === 0) return '';

  return `
    <section class="panel">
      <div class="section-bar">
        <div class="section-bar-left"><h3>Reminders to send</h3></div>
        <span class="muted">${pending.length} waiting</span>
      </div>
      <p class="muted" style="margin-bottom:12px;font-size:12.5px">
        Sent from your own phone, so replies come back to you. Tapping Send opens
        your share sheet with the message ready.
      </p>
      <ul class="reminders">
        ${pending.map((n) => {
          const msg = buildReminderMessage(n);
          const url = linkFor(n, portalTokens);
          const phone = reminderPhone(n);
          const who = String((n.payload as Record<string, unknown>)?.first_name ?? '') || 'contact';
          return `
            <li class="reminder-item">
              <div class="reminder-body">
                <div class="reminder-head">
                  <strong>${esc(who)}</strong>
                  <span class="chip chip-tag">${esc(reminderKindLabel(n.kind))}</span>
                  <span class="muted">${esc(fmtDate(n.scheduled_for))}</span>
                </div>
                <div class="muted reminder-preview">${esc(msg)}</div>
                ${phone ? '' : '<div class="reminder-warn">No phone on file — the share sheet will ask who to send to.</div>'}
              </div>
              <div class="reminder-actions">
                <button class="btn-primary btn-sm" data-send-reminder="${esc(n.id)}"
                        data-msg="${esc(msg)}" data-url="${esc(url ?? '')}"
                        data-phone="${esc(phone ?? '')}">Send</button>
                <button class="btn-link" data-dismiss-reminder="${esc(n.id)}">Dismiss</button>
              </div>
            </li>`;
        }).join('')}
      </ul>
    </section>`;
}

function renderUpcoming(
  upcoming: ScheduleEntryRow[],
  rooms: RoomRow[],
  contacts: DesignerContactRow[],
  assignments: ContractorAssignmentRow[],
): string {
  if (upcoming.length === 0) {
    return `<div class="empty-inline">Nothing scheduled yet. Add a task to see it here.</div>`;
  }
  const roomName = new Map(rooms.map((r) => [r.id, r.name] as const));
  const contactName = new Map(contacts.map((c) => [c.id, c.name] as const));

  return `<ul class="upcoming">
    ${upcoming.map((e) => {
      const who = assignments
        .filter((a) => a.schedule_entry_id === e.id)
        .map((a) => contactName.get(a.contact_id))
        .filter(Boolean);
      const when = e.start_date
        ? fmtDate(e.start_date) + (e.end_date && e.end_date !== e.start_date ? ` → ${fmtDate(e.end_date)}` : '')
        : 'Unscheduled';
      return `
        <li class="upcoming-item">
          <div class="upcoming-when ${e.start_date ? '' : 'muted'}">${esc(when)}</div>
          <div class="upcoming-body">
            <div class="upcoming-task">${esc(e.task)}</div>
            <div class="muted">
              ${esc(roomName.get(e.room_id) ?? '—')}${who.length ? ' · ' + esc(who.join(', ')) : ''}
            </div>
          </div>
          <span class="chip chip-${esc(e.status)}">${esc(SCHEDULE_STATUS_LABEL[e.status])}</span>
        </li>`;
    }).join('')}
  </ul>`;
}

function renderAllSpecsTable(
  specs: SpecRow[],
  rooms: RoomRow[],
  categories: MaterialCategoryRow[],
  cats: ReturnType<typeof indexMaterialCategories>,
): string {
  if (specs.length === 0) {
    return `<div class="empty-inline">No materials yet. Add materials from inside each room.</div>`;
  }
  const roomName = new Map(rooms.map((r) => [r.id, r.name] as const));
  const ctx = {
    roomName: (id: string) => roomName.get(id) ?? '',
    categoryName: (id: string | null | undefined) => cats.name(id),
  };

  const visible = sortSpecs(
    filterSpecs(specs, { categoryId: specCategoryFilter, roomId: specRoomFilter }),
    specSort.key, specSort.dir, ctx,
  );

  const uncategorized = specs.filter((s) => s.material_category_id == null).length;

  const th = (key: SpecSortKey, label: string): string => {
    const active = specSort.key === key;
    return `<th class="th-sort ${active ? 'is-sorted' : ''}" data-sort="${key}"
      aria-sort="${active ? (specSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}">
      ${esc(label)}<span class="sort-caret">${active ? (specSort.dir === 'asc' ? '▲' : '▼') : ''}</span>
    </th>`;
  };

  const filters = `
    <div class="table-filters">
      <label class="table-filter">
        <span>Category</span>
        <select data-spec-cat-filter>
          <option value="all"${specCategoryFilter === 'all' ? ' selected' : ''}>All categories</option>
          ${uncategorized > 0
            ? `<option value="none"${specCategoryFilter === 'none' ? ' selected' : ''}>Uncategorized (${uncategorized})</option>`
            : ''}
          ${categories.map((c) => `<option value="${esc(c.id)}"${specCategoryFilter === c.id ? ' selected' : ''}>${esc(c.name)}</option>`).join('')}
        </select>
      </label>
      <label class="table-filter">
        <span>Room</span>
        <select data-spec-room-filter>
          <option value="all"${specRoomFilter === 'all' ? ' selected' : ''}>All rooms</option>
          ${rooms.map((r) => `<option value="${esc(r.id)}"${specRoomFilter === r.id ? ' selected' : ''}>${esc(r.name)}</option>`).join('')}
        </select>
      </label>
      <span class="muted table-count">${visible.length} of ${specs.length}</span>
    </div>`;

  const body = visible.length === 0
    ? `<div class="empty-inline">No materials match these filters.</div>`
    : `<div class="table-wrap"><table class="specs-table">
        <thead><tr>
          ${th('item', 'Item')}${th('room', 'Room')}${th('vendor', 'Vendor')}
          ${th('category', 'Category')}<th>Qty</th>${th('cost', 'Cost')}
          ${th('status', 'Status')}${th('delivery', 'Est. delivery')}
        </tr></thead>
        <tbody>${visible.map((s) => {
          const catName = cats.name(s.material_category_id);
          return `<tr>
            <td><a href="#/rooms/${esc(s.room_id)}" class="spec-item-link">${esc(s.item)}</a></td>
            <td>${esc(roomName.get(s.room_id) ?? '—')}</td>
            <td>${esc(s.vendor ?? '—')}</td>
            <td>${catName ? esc(catName) : '<span class="muted">—</span>'}</td>
            <td>${esc(String(s.quantity))}${s.unit ? ' ' + esc(s.unit) : ''}</td>
            <td>${fmtMoney(extendedCostCents(s))}</td>
            <td><span class="chip chip-${esc(s.status)}">${esc(SPEC_STATUS_LABEL[s.status])}</span></td>
            <td>${fmtDate(s.estimated_delivery)}</td>
          </tr>`;
        }).join('')}</tbody>
      </table></div>`;

  return filters + body;
}

function renderQuestionnairePanel(q: QuestionnaireRow | null): string {
  if (!q) {
    return `<div class="empty-inline">No questionnaire yet. Capture style words, must-haves, deal-breakers and inspiration to brief the project.</div>`;
  }
  const r = readResponses(q);
  const filled = QUESTIONNAIRE_FIELDS.filter((f) => (r[f.key] ?? '').trim() !== '');
  if (filled.length === 0) {
    return `<div class="empty-inline">Questionnaire started but no answers yet — click Edit to fill it in.</div>`;
  }
  return `
    <div class="questionnaire-summary">
      ${filled.map((f) => `
        <div class="q-summary">
          <div class="q-label">${esc(f.label)}</div>
          <div class="q-answer">${esc(r[f.key] ?? '')}</div>
        </div>`).join('')}
    </div>
    ${q.submitted_at ? `<div class="muted small">Submitted ${esc(fmtDate(q.submitted_at))}</div>` : ''}
  `;
}

function renderNotificationsPanel(
  rows: NotificationRow[],
  contacts: DesignerContactRow[],
  portalTokens: Map<string, ContractorPortalTokenRow>,
): string {
  if (rows.length === 0) {
    return `<div class="empty-inline">
      No notifications yet. Assign a subcontractor to a scheduled job and reminders
      will queue automatically once you've set up SMS — see <code>SETUP_NOTIFICATIONS.md</code>.
    </div>`;
  }
  const contactName = (id: string | null): string => {
    if (!id) return '—';
    const c = contacts.find((x) => x.id === id);
    return c?.name ?? '(removed)';
  };
  return `
    <ul class="notifications-list">
      ${rows.map((n) => `
        <li class="notification-row">
          <div class="notification-row-left">
            <span class="chip chip-${esc(n.status)}">${esc(n.status)}</span>
            <div>
              <div>${esc(NOTIFICATION_KIND_LABEL[n.kind] ?? n.kind)} → ${esc(contactName(n.contact_id))}</div>
              <div class="muted small">
                ${esc(notificationStamp(n))}
                ${n.error ? ` · <span style="color:var(--danger)">${esc(n.error)}</span>` : ''}
              </div>
              <div class="muted small notification-preview">${esc(buildReminderMessage(n))}</div>
            </div>
          </div>
          ${canResend(n) ? `
            <button class="btn-link" data-resend="${esc(n.id)}"
                    data-msg="${esc(buildReminderMessage(n))}"
                    data-url="${esc(linkFor(n, portalTokens) ?? '')}"
                    data-phone="${esc(reminderPhone(n) ?? '')}">${esc(resendLabel(n))}</button>` : ''}
        </li>`).join('')}
    </ul>
  `;
}

function renderChangeOrdersTable(
  rows: ChangeOrderRow[], rooms: RoomRow[],
  awaitingAnswer: Set<string> = new Set(), events: ChangeOrderEventRow[] = [],
): string {
  if (rows.length === 0) {
    return `<div class="empty-inline">
      No change orders yet. Use a change order to document scope or cost changes
      and notify the client.
    </div>`;
  }
  const roomName = (id: string): string => rooms.find((r) => r.id === id)?.name ?? '?';
  return `
    <div class="table-wrap"><table class="specs-table">
      <thead><tr>
        <th>#</th><th>Title</th><th class="num">Work</th><th class="num">Design fee</th><th>Affects</th>
        <th>Status</th><th>Sent</th><th></th>
      </tr></thead>
      <tbody>
        ${rows.map((co) => {
          const a = changeOrderActions(co);
          const approved = approvalLine(co);
          // The badge the designer needs: a client asked something and nobody
          // has answered. The set is a fast path only — unansweredCount is the
          // decision, and it answers the same way without the guard.
          const waiting = awaitingAnswer.has(co.id)
            ? awaitingAnswerLabel(unansweredCount(events, co.id))
            : null;
          return `
          <tr>
            <td><strong>CO #${co.number}</strong></td>
            <td>
              <button class="spec-name co-title" data-co-detail="${esc(co.id)}"
                      title="Open this change order">${esc(co.title)}</button>
              ${approved ? `<div class="spec-vendor">${esc(approved)}</div>` : ''}
              ${co.status === 'acknowledged' ? '<div class="muted small">Locked — revise it to change anything</div>' : ''}
              ${waiting ? `<div class="co-waiting">${esc(waiting)}</div>` : ''}
            </td>
            <td class="num">${esc(signedMoney(co.cost_delta_cents))}</td>
            <td class="num">${esc(signedMoney(co.designer_fee_cents))}</td>
            <td>${co.affected_room_ids.length === 0
              ? '<span class="muted">All</span>'
              : co.affected_room_ids.slice(0, 3).map((id) => esc(roomName(id))).join(', ') +
                (co.affected_room_ids.length > 3 ? ` +${co.affected_room_ids.length - 3}` : '')}</td>
            <td><span class="chip chip-${esc(co.status)}">${esc(CHANGE_ORDER_STATUS_LABEL[co.status])}</span></td>
            <td>${co.sent_at ? esc(fmtDate(co.sent_at)) : '<span class="muted">—</span>'}</td>
            <td class="co-actions">
              ${a.canSend ? `<button class="btn-ghost" data-send-co="${esc(co.id)}" title="Send to client">Send</button>` : ''}
              ${a.canRecordApproval ? `<button class="btn-link" data-approve-co="${esc(co.id)}" title="Record an approval received by email or phone">Mark approved</button>` : ''}
              ${a.canDecline ? `<button class="btn-link" data-close-co="${esc(co.id)}" data-close-as="rejected">Declined</button>` : ''}
              ${a.canWithdraw ? `<button class="btn-link btn-danger-link" data-close-co="${esc(co.id)}" data-close-as="cancelled">Withdraw</button>` : ''}
              ${a.canReopen ? `<button class="btn-link" data-reopen-co="${esc(co.id)}" title="Put it back in play as a draft, keeping its history">Reopen</button>` : ''}
              ${waiting ? `<button class="btn-link" data-answer-co="${esc(co.id)}" title="Answer the client">Answer</button>` : ''}
              ${a.canEdit ? `<button class="icon-btn" data-edit-co="${esc(co.id)}" title="Edit">✎</button>` : ''}
              ${a.canDelete ? `<button class="icon-btn" data-delete-co="${esc(co.id)}" title="Move to Deleted">🗑</button>` : ''}
            </td>
          </tr>`;
        }).join('')}
      </tbody>
    </table></div>
  `;
}

/**
 * The bin (0046). Collapsed by default — it is a safety net, not a section
 * anyone reads — and it renders nothing at all when it is empty, rather than
 * putting a permanent empty "Deleted" heading on the page.
 */
function renderDeletedChangeOrders(rows: ChangeOrderRow[], rooms: RoomRow[]): string {
  if (rows.length === 0) return '';
  const roomName = (id: string): string => rooms.find((r) => r.id === id)?.name ?? '?';
  return `
    <details class="co-deleted">
      <summary>Deleted <span class="muted small">(${rows.length})</span></summary>
      <p class="muted small">Hidden from the project and from the client. Restore one to work on it again.</p>
      <div class="table-wrap"><table class="specs-table">
        <thead><tr>
          <th>#</th><th>Title</th><th class="num">Total</th><th>Affects</th><th>Was</th><th>Deleted</th><th></th>
        </tr></thead>
        <tbody>
          ${rows.map((co) => `
            <tr>
              <td><strong>CO #${co.number}</strong></td>
              <td><button class="spec-name co-title" data-co-detail="${esc(co.id)}"
                          title="Open this change order">${esc(co.title)}</button></td>
              <td class="num">${esc(signedMoney(changeOrderTotal(co)))}</td>
              <td>${co.affected_room_ids.length === 0
                ? '<span class="muted">All</span>'
                : co.affected_room_ids.slice(0, 3).map((id) => esc(roomName(id))).join(', ')}</td>
              <td><span class="chip chip-${esc(co.status)}">${esc(CHANGE_ORDER_STATUS_LABEL[co.status])}</span></td>
              <td>${co.deleted_at ? esc(fmtDate(co.deleted_at)) : '<span class="muted">—</span>'}</td>
              <td class="co-actions">
                <button class="btn-link" data-restore-co="${esc(co.id)}">Restore</button>
              </td>
            </tr>`).join('')}
        </tbody>
      </table></div>
    </details>`;
}

function renderSharePanel(p: ProjectRow): string {
  if (!p.share_token) {
    return `
      <div class="share-panel">
        <p class="muted">Generate a read-only link to send to the client. They'll see the project, rooms, materials, upcoming work, palette, and notes — but cannot edit anything.</p>
        <div class="share-row">
          <label class="field" style="flex:1">
            <span>Expires (optional)</span>
            <input type="date" id="share-expiry" />
          </label>
          <button class="btn-primary" data-action="share-generate">Generate link</button>
        </div>
      </div>
    `;
  }
  const url = buildShareUrl(p.share_token);
  const expires = p.share_token_expires_at
    ? `Expires ${fmtDate(p.share_token_expires_at)}`
    : 'No expiration set';
  return `
    <div class="share-panel">
      <div class="share-url-row">
        <input class="share-url" value="${esc(url)}" readonly onclick="this.select()" />
        <button class="btn-primary" data-action="share-copy">Copy</button>
      </div>
      <div class="share-meta">
        <span class="muted">${esc(expires)}</span>
        <span class="dot">·</span>
        <button class="btn-link" data-action="share-generate">Regenerate</button>
        <span class="dot">·</span>
        <button class="btn-link btn-danger-link" data-action="share-revoke">Revoke</button>
      </div>
      <div class="share-row" style="margin-top:8px">
        <label class="field" style="flex:1">
          <span>Update expiry (leave blank for none)</span>
          <input type="date" id="share-expiry" value="${esc(p.share_token_expires_at?.slice(0, 10) ?? '')}" />
        </label>
      </div>
    </div>
  `;
}

function pickFile(accept: string, onPick: (file: File) => Promise<void>): void {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = accept;
  input.addEventListener('change', async () => {
    const file = input.files?.[0];
    if (!file) return;
    try { await onPick(file); }
    catch (err) { toastError(err instanceof Error ? err.message : 'Upload failed'); }
  });
  input.click();
}

// isMaster is REQUIRED on purpose. With a default, `contacts.map(renderContact)`
// type-checks and silently passes the array INDEX as isMaster — badging every
// contact after the first. Required, the compiler rejects that form outright.
function renderContact(
  c: DesignerContactRow,
  isMaster: boolean,
  portalToken: ContractorPortalTokenRow | null,
  projectName: string,
): string {
  // Built at render time, not on click — see the note on the reminders panel.
  const portalHref = portalToken ? buildContractorUrl(portalToken.token) : null;
  const portalMsg = portalHref ? buildPortalInviteMessage(c.name, projectName) : null;
  const tradeLabel = c.trade ? TRADE_LABEL[c.trade] : null;
  // The four flags replaced the single role in 0018; `role` is still shown as
  // a fallback for contacts that only ever had free text.
  const eyebrow = [
    contactTypeLabel(c) || c.role,
    c.is_subcontractor ? tradeLabel : null,
  ].filter(Boolean).join(' · ');
  // Precomputed lowercase haystack so the search box can filter without
  // re-deriving it or leaning on textContent (which also picks up button labels).
  const searchKey = [contactSearchKey(c), c.role, tradeLabel]
    .filter(Boolean).join(' ').toLowerCase();
  return `
    <li class="contact" data-search="${esc(searchKey)}">
      <span class="contact-avatar" aria-hidden="true">${esc(contactInitials(c.name))}</span>
      <div>
        ${eyebrow ? `<div class="contact-role">${esc(eyebrow)}</div>` : ''}
        <div class="contact-name">${esc(c.name)}${
          isMaster ? ` <span class="chip chip-approved">Master contractor</span>` : ''
        }</div>
        <div class="muted">${esc(c.company ?? '—')}</div>
        ${c.email ? `<div class="contact-line"><a href="mailto:${esc(c.email)}">${esc(c.email)}</a></div>` : ''}
        ${c.phone ? `<div class="contact-line"><a href="tel:${esc(c.phone)}">${esc(c.phone)}</a></div>` : ''}
      </div>
      ${portalHref ? `<button class="btn-ghost" data-send-portal="${esc(c.id)}"
              data-msg="${esc(portalMsg ?? '')}" data-url="${esc(portalHref)}"
              data-phone="${esc(c.phone ?? '')}">Send link</button>` : ''}
      <button class="btn-ghost" data-contractor-portal="${esc(c.id)}" title="Portal &amp; assignments">Portal</button>
      <button class="icon-btn" data-edit-contact="${esc(c.id)}" title="Edit">✎</button>
    </li>
  `;
}

const NOTE_TAG_LABEL: Record<NoteTag, string> = {
  design: 'Design', client: 'Client', budget: 'Budget',
  timeline: 'Timeline', vendor: 'Vendor', general: 'General',
};

function renderNote(n: NoteRow): string {
  return `
    <li class="note ${n.pinned ? 'note-pinned' : ''}">
      ${n.title ? `<div class="note-title">${n.pinned ? '📌 ' : ''}${esc(n.title)}</div>` : ''}
      <div class="note-body">${!n.title && n.pinned ? '📌 ' : ''}${esc(n.body)}</div>
      <div class="note-foot">
        ${n.tag ? `<span class="chip chip-tag">${esc(NOTE_TAG_LABEL[n.tag])}</span>` : ''}
        <span class="muted">${fmtDate(n.created_at)}</span>
        <button class="btn-link" data-send-note="${esc(n.id)}"
                title="Send this note to a client or contractor">Send</button>
        <button class="icon-btn" data-edit-note="${esc(n.id)}" title="Edit">✎</button>
      </div>
    </li>
  `;
}


// ─── Modal openers ─────────────────────────────────────────────────────────

/**
 * "Add from my contacts" — the designer's library, minus whoever is already on
 * this project.
 *
 * This is the payoff for migration 0018: a subcontractor entered once on a
 * previous job is two clicks away on the next one, and keeps the same identity,
 * so their assignments and portal links stay coherent across projects.
 */
async function openContactPicker(opts: {
  projectId: string;
  alreadyOn: Set<string>;
  onDone: () => void;
}): Promise<void> {
  let library: DesignerContactRow[];
  try {
    library = await listDesignerContacts();
  } catch (err) {
    toastError(err instanceof Error ? err.message : 'Could not load your contacts');
    return;
  }
  const available = library.filter((c) => !opts.alreadyOn.has(c.id));

  if (available.length === 0) {
    toastSuccess(library.length === 0
      ? 'Your contact library is empty — use "+ Add" to create one.'
      : 'Everyone in your contacts is already on this project.');
    return;
  }

  const overlay = document.createElement('div');
  overlay.className = 'modal';
  overlay.innerHTML = `
    <div class="modal-card">
      <h2>Add from my contacts</h2>
      <input class="picker-search" type="search" placeholder="Search contacts…" />
      <ul class="picker-list">
        ${available.map((c) => `
          <li data-search="${esc(contactSearchKey(c))}">
            <button type="button" data-pick="${esc(c.id)}">
              <span class="picker-name">${esc(c.name)}</span>
              <span class="muted">${esc(contactSummary(c))}</span>
            </button>
          </li>`).join('')}
      </ul>
      <div class="picker-empty" hidden>No matches.</div>
      <div class="modal-actions"><button class="btn-link" data-action="close">Cancel</button></div>
    </div>`;
  document.body.appendChild(overlay);

  const close = (): void => overlay.remove();
  overlay.querySelector('[data-action="close"]')!.addEventListener('click', close);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  const search = overlay.querySelector<HTMLInputElement>('.picker-search')!;
  search.addEventListener('input', () => {
    const q = search.value.trim().toLowerCase();
    let visible = 0;
    overlay.querySelectorAll<HTMLElement>('.picker-list li').forEach((li) => {
      const match = q === '' || (li.dataset.search ?? '').includes(q);
      li.hidden = !match;
      if (match) visible++;
    });
    overlay.querySelector<HTMLElement>('.picker-empty')!.hidden = visible > 0;
  });
  search.focus();

  overlay.querySelectorAll<HTMLButtonElement>('[data-pick]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      try {
        await addContactToProject(opts.projectId, btn.dataset.pick!);
        close();
        opts.onDone();
      } catch (err) {
        toastError(err instanceof Error ? err.message : 'Could not add contact');
      }
    });
  });
}

async function openContractorPortalModal(opts: {
  contact: DesignerContactRow;
  rooms: RoomRow[];
  schedule: ScheduleEntryRow[];
  onDone: () => void;
}): Promise<void> {
  const { contact, rooms, schedule, onDone } = opts;

  // Portal links are per (contact, project) since 0018 — this panel always
  // acts on the project whose page it was opened from.
  const projectId = rooms[0]?.project_id ?? '';
  // Load current state (token + assignments).
  let token = await getActivePortalToken(contact.id, projectId);
  const allAssignments = await listAssignmentsForProject(projectId);
  const mineSet = new Set(
    allAssignments
      .filter((a) => a.contact_id === contact.id)
      .map((a) => a.schedule_entry_id),
  );

  // Default scope: subcontractors usually want 'assigned' (only their jobs).
  // Everyone else (GC, architect, vendor) typically wants 'all' (whole project).
  // If a token already exists, use its scope; otherwise fall back to the role-based default.
  let viewScope: 'assigned' | 'all' = token?.view_scope
    ?? (contact.is_subcontractor ? 'assigned' : 'all');

  const overlay = document.createElement('div');
  overlay.className = 'modal';

  const close = (): void => {
    document.removeEventListener('keydown', onKey);
    overlay.remove();
    onDone();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') { e.preventDefault(); close(); }
  };
  document.addEventListener('keydown', onKey);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

  const renderShellHtml = (): string => {
    const url = token ? buildContractorUrl(token.token) : '';
    const entryRows = schedule.map((se) => {
      const room = rooms.find((r) => r.id === se.room_id);
      const checked = mineSet.has(se.id) ? 'checked' : '';
      const dateLabel = se.start_date ? fmtDate(se.start_date) : '(no date)';
      return `<label class="field-checkbox" style="padding:6px 0;border-bottom:1px solid var(--border)">
        <input type="checkbox" data-assign-entry="${esc(se.id)}" ${checked} />
        <span style="flex:1">
          <span>${esc(se.task)}</span>
          <span class="muted" style="font-size:12px"> · ${esc(room?.name ?? 'Unknown room')} · ${esc(dateLabel)}</span>
        </span>
      </label>`;
    }).join('');

    return `
      <form class="modal-card" novalidate style="max-width:560px">
        <h2>Portal — ${esc(contact.name)}</h2>

        <div class="field" style="margin-top:8px">
          <span>What this contact can see</span>
          <div class="scope-toggle">
            <label class="scope-option ${viewScope === 'all' ? 'is-active' : ''}">
              <input type="radio" name="view-scope" value="all" ${viewScope === 'all' ? 'checked' : ''} />
              <strong>All rooms</strong>
              <small>Whole project. Good for GCs, architects, vendors.</small>
            </label>
            <label class="scope-option ${viewScope === 'assigned' ? 'is-active' : ''}">
              <input type="radio" name="view-scope" value="assigned" ${viewScope === 'assigned' ? 'checked' : ''} />
              <strong>Assigned only</strong>
              <small>Only the rooms tied to scheduled jobs you check below. Best for trade subs.</small>
            </label>
          </div>
        </div>

        <div class="field" style="margin-top:16px">
          <span>Portal link</span>
          ${token ? `
            <div style="display:flex;gap:6px;align-items:center">
              <input type="text" value="${esc(url)}" readonly id="cp-url" style="flex:1;font-family:monospace;font-size:12px" />
              <button type="button" class="btn-ghost" data-action="copy">Copy</button>
            </div>
            <div class="muted" style="font-size:11px;margin-top:6px">
              Created ${fmtDate(token.created_at)} · scope: <strong>${esc(token.view_scope)}</strong>${token.expires_at ? ' · expires ' + fmtDate(token.expires_at) : ' · no expiry'}
            </div>
          ` : `
            <div class="muted" style="font-size:13px">No portal link yet.</div>
          `}
          <div style="margin-top:8px;display:flex;gap:8px">
            <button type="button" class="btn-ghost" data-action="rotate">
              ${token ? 'Generate new link' : 'Generate portal link'}
            </button>
            ${token ? `<button type="button" class="btn-danger" data-action="revoke">Revoke</button>` : ''}
          </div>
        </div>

        ${viewScope === 'all' ? '' : `
          <div class="field" style="margin-top:16px">
            <span>Assigned scheduled work</span>
            ${schedule.length === 0
              ? `<div class="muted" style="font-size:13px">No scheduled entries in this project yet. Add scheduled work in a room's Schedule tab.</div>`
              : `<div style="max-height:300px;overflow-y:auto;border:1px solid var(--border);border-radius:6px;padding:8px 12px">
                  ${entryRows}
                </div>
                <div class="muted" style="font-size:11px;margin-top:6px">
                  Check the work items this contact is responsible for. They'll get SMS reminders at T-7d / T-2d / day-of for each one.
                </div>`}
          </div>
        `}

        <div class="modal-error" hidden></div>
        <div class="modal-actions">
          <span style="flex:1"></span>
          <button type="button" class="btn-primary" data-action="done">Done</button>
        </div>
      </form>
    `;
  };

  const rerender = (): void => {
    overlay.innerHTML = renderShellHtml();
    wire();
  };

  const wire = (): void => {
    overlay.querySelector('[data-action="done"]')!.addEventListener('click', close);
    overlay.querySelectorAll('[data-action="copy"]').forEach((el) => {
      el.addEventListener('click', async () => {
        const input = overlay.querySelector<HTMLInputElement>('#cp-url');
        if (!input) return;
        try { await navigator.clipboard.writeText(input.value); }
        catch { input.select(); document.execCommand('copy'); }
        const btn = el as HTMLButtonElement;
        const orig = btn.textContent;
        btn.textContent = 'Copied!';
        setTimeout(() => { btn.textContent = orig; }, 1500);
      });
    });
    overlay.querySelectorAll<HTMLInputElement>('input[name="view-scope"]').forEach((r) => {
      r.addEventListener('change', () => {
        if (r.checked) {
          viewScope = r.value as 'assigned' | 'all';
          rerender();
        }
      });
    });
    overlay.querySelector('[data-action="rotate"]')?.addEventListener('click', async () => {
      try {
        await rotateContractorToken(contact.id, projectId, null, viewScope);
        token = await getActivePortalToken(contact.id, projectId);
        rerender();
      } catch (err) { showModalErr(overlay, err); }
    });
    overlay.querySelector('[data-action="revoke"]')?.addEventListener('click', async () => {
      if (!confirm('Revoke this contractor link? They will lose access immediately.')) return;
      try {
        await clearContractorToken(contact.id, projectId);
        token = null;
        rerender();
      } catch (err) { showModalErr(overlay, err); }
    });
    overlay.querySelectorAll<HTMLInputElement>('[data-assign-entry]').forEach((cb) => {
      cb.addEventListener('change', async () => {
        const entryId = cb.dataset.assignEntry!;
        try {
          if (cb.checked) {
            await assignContractor(contact.id, entryId);
            mineSet.add(entryId);
          } else {
            // Find the assignment id to delete (RLS allows by contact owner).
            const a = (allAssignments.find(
              (x) => x.contact_id === contact.id && x.schedule_entry_id === entryId,
            )) as ContractorAssignmentRow | undefined;
            if (a) await unassignContractor(a.id);
            mineSet.delete(entryId);
          }
        } catch (err) {
          cb.checked = !cb.checked;
          showModalErr(overlay, err);
        }
      });
    });
  };

  rerender();
  document.body.append(overlay);
}

function showModalErr(overlay: HTMLElement, err: unknown): void {
  const box = overlay.querySelector<HTMLDivElement>('.modal-error');
  if (!box) return;
  box.hidden = false;
  box.textContent = saveErrorMessage(err, navigator.onLine !== false);
}

function openChangeOrderModal(opts: {
  projectId: string;
  rooms: RoomRow[];
  co?: ChangeOrderRow;
  onDone: () => void;
}): void {
  const co = opts.co;
  // Affected rooms are encoded as a comma-separated string in the form; we
  // serialize/deserialize around the simple modal API.
  openModal({
    title: co ? `Edit Change Order #${co.number}` : 'New change order',
    showDelete: !!co,
    submitLabel: 'Save',
    fields: [
      { name: 'title', label: 'Title', required: true, defaultValue: co?.title },
      { name: 'scope', label: 'Scope description', type: 'textarea', voice: true, voiceKind: 'scope',
        required: true, defaultValue: co?.scope },
      { name: 'cost_delta_dollars', label: 'Cost of the work (USD, can be negative)', type: 'number',
        defaultValue: co ? Math.round(co.cost_delta_cents / 100) : 0 },
      { name: 'designer_fee_dollars', label: 'Additional design fee (USD, optional)', type: 'number',
        defaultValue: co ? Math.round(co.designer_fee_cents / 100) : 0 },
      { name: 'affected_room_ids', label: 'Affected rooms', type: 'select',
        defaultValue: (co?.affected_room_ids?.[0]) ?? '',
        options: [
          { value: '', label: '— All rooms —' },
          ...opts.rooms.map((r) => ({ value: r.id, label: r.name })),
        ] },
      { name: 'notes', label: 'Internal notes (optional)', type: 'textarea', voice: true,
        defaultValue: co?.notes ?? '' },
    ],
    onSubmit: async (v) => {
      const dollars = Number(v.cost_delta_dollars) || 0;
      const affected = String(v.affected_room_ids ?? '');
      const payload = {
        title: String(v.title),
        scope: String(v.scope),
        cost_delta_cents: Math.round(dollars * 100),
        designer_fee_cents: Math.round((Number(v.designer_fee_dollars) || 0) * 100),
        affected_room_ids: affected ? [affected] : [],
        notes: emptyToNull(v.notes),
      };
      if (co) await updateChangeOrder(co.id, payload);
      else await createChangeOrder({ project_id: opts.projectId, ...payload });
      opts.onDone();
    },
    onDelete: co ? async () => { await deleteChangeOrder(co.id); opts.onDone(); } : undefined,
  });
}

function openNoteModal(opts: { projectId: string; note?: NoteRow; onDone: () => void }): void {
  const n = opts.note;
  openModal({
    title: n ? 'Edit note' : 'Add note',
    showDelete: !!n,
    fields: [
      { name: 'title', label: 'Title', defaultValue: n?.title },
      { name: 'tag', label: 'Tag', type: 'select', defaultValue: n?.tag ?? '', options: [
        { value: '', label: '— None —' },
        ...(Object.keys(NOTE_TAG_LABEL) as NoteTag[]).map((k) => ({ value: k, label: NOTE_TAG_LABEL[k] })),
      ] },
      { name: 'body', label: 'Note', type: 'textarea', voice: true, required: true, defaultValue: n?.body },
      { name: 'pinned', label: 'Pin to top', type: 'checkbox', defaultValue: n?.pinned ?? false },
    ],
    onSubmit: async (v) => {
      const tagVal = String(v.tag);
      const payload = {
        title: emptyToNull(v.title),
        tag: (tagVal === '' ? null : tagVal as NoteTag),
        body: String(v.body),
        pinned: !!v.pinned,
      };
      if (n) await updateNote(n.id, payload);
      else await createNote({ project_id: opts.projectId, ...payload });
      opts.onDone();
    },
    onDelete: n ? async () => { await deleteNote(n.id); opts.onDone(); } : undefined,
  });
}



function openQuestionnaireModal(opts: {
  projectId: string;
  existing: QuestionnaireRow | null;
  onDone: () => void;
}): void {
  const r = readResponses(opts.existing);
  openModal({
    title: opts.existing ? 'Edit style questionnaire' : 'Style questionnaire',
    submitLabel: 'Save',
    fields: [
      ...QUESTIONNAIRE_FIELDS.map((f) => ({
        name: f.key,
        label: f.label,
        type: 'textarea' as const,
        // "Other notes" is dictated like every Notes field; the style answers
        // stay typed.
        voice: f.key === 'notes',
        placeholder: f.placeholder,
        defaultValue: r[f.key] ?? '',
      })),
      {
        name: 'mark_submitted',
        label: 'Mark as submitted by client',
        type: 'checkbox' as const,
        defaultValue: !!opts.existing?.submitted_at,
      },
    ],
    onSubmit: async (v) => {
      const responses: QuestionnaireResponses = {};
      for (const f of QUESTIONNAIRE_FIELDS) {
        const val = String(v[f.key] ?? '').trim();
        if (val) responses[f.key as QuestionnaireKey] = val;
      }
      const wasSubmitted = !!opts.existing?.submitted_at;
      const markSubmitted = !!v.mark_submitted && !wasSubmitted;
      await upsertQuestionnaire(opts.projectId, responses, opts.existing?.id ?? null, markSubmitted);
      opts.onDone();
    },
  });
}

